import { getWithIDToken, IMSTokenParams } from "@utils/http/MSAuthAPI";
import { Country } from '@utils/constants/localisation';
import { PHAVRecordDetails } from './PHAVService.types';

/**
 * Service interface for PHAV operations
 *
 * This service handles communication with the PHAV API endpoints to retrieve
 * PHAV record details from Zoho CRM.
 */
interface IPHAVService {
    /**
     * Retrieve PHAV record details by ID
     *
     * @param phavId - The Zoho PHAV record ID
     * @param country - The country code (e.g., "GB")
     * @param IDTokenParams - Microsoft ID token parameters for authentication
     * @returns Promise resolving to PHAV record details
     * @throws Error if the API request fails
     */
    getPHAVRecordDetails(
        phavId: string,
        country: Country,
        IDTokenParams: IMSTokenParams
    ): Promise<PHAVRecordDetails>;
}

const PHAVService: IPHAVService = {
    /**
     * Fetch PHAV record details from the API
     *
     * Makes a GET request to /v1/phav/{phav_id} with country as a query parameter.
     *
     * @param phavId - The Zoho PHAV record ID
     * @param country - The country code
     * @param IDTokenParams - Authentication parameters
     * @returns Promise resolving to the PHAV record details
     * @throws Error if the server returns a 500+ status code
     */
    async getPHAVRecordDetails(
        phavId: string,
        country: Country,
        IDTokenParams: IMSTokenParams
    ): Promise<PHAVRecordDetails> {
        const resp = await getWithIDToken(
            `/v1/phav/${phavId}`,
            IDTokenParams,
            { country: country }
        );

        if (resp.status >= 500) {
            throw new Error('Server error while fetching PHAV record details');
        }

        return resp.data;
    }
};

export default PHAVService;